import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { FieldArray } from 'react-final-form-arrays';
import {
  flatten,
} from 'lodash';

import {
  stripesConnect,
} from '@folio/stripes/core';
import {
  Accordion,
} from '@folio/stripes/components';

import {
  AssignTask,
} from './components';
import {
  composeValidators,
  validateFieldLength,
  validateRequiredField,
} from '../../../../../utils';
import {
  useCallout,
} from '../../../../../hooks';
import {
  JOB_FIELDS,
  ASSIGN_TASK_FIELDS,
  NAME_LIMIT_MAX,
  DESCRIPTION_LIMIT_MAX,
  LOAD_LIMIT,
  LOAD_LIMIT_MAX,
} from '../../../../../constants';

const validateNameLength = value => validateFieldLength(value, NAME_LIMIT_MAX);
const validateDescriptionLength = value => validateFieldLength(value, DESCRIPTION_LIMIT_MAX);

const AssignTaskList = ({
  canAssignedTaskStatusChange,
  change,
  mutator,
}) => {
  const showCallout = useCallout();

  const [teams, setTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [users, setUsers] = useState({});

  const getTeams = useCallback(() => {
    setTeamsLoaded(false);

    mutator.teams.GET({
      params: {
        limit: LOAD_LIMIT_MAX,
      },
    })
      .then((response) => {
        setTeams(response.teams);
        setTeamsLoaded(true);
      })
      .catch(() => {
        setTeams([]);

        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  }, []);

  const getUsers = (value) => {
    const selectedTeamObj = teams.find((team) => team.id === value);
    const userIds = selectedTeamObj?.members
      ? [...selectedTeamObj.members]
      : [];
    const usersPromises = [];

    while (userIds.length) {
      usersPromises.push(mutator.users.GET({
        params: {
          limit: LOAD_LIMIT,
          query: userIds.splice(0, LOAD_LIMIT)
            .map(userId => `id==${userId}`)
            .join(' or '),
        }
      }));
    }

    return Promise.all(usersPromises)
      .then((userArrays) => {
        const combinedUsers = flatten(userArrays.map((arr) => arr.users));

        setUsers((state) => ({
          ...state,
          [value]: combinedUsers,
        }));
      })
      .catch(() => {
        setUsers((state) => ({
          ...state,
          [value]: [],
        }));

        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  };

  useEffect(
    () => {
      getTeams();
    },
    [],
  );

  const validate = (values) => {
    const errorArray = [];

    if (values) {
      values.forEach((value) => {
        const name = value[ASSIGN_TASK_FIELDS.NAME];
        const description = value[ASSIGN_TASK_FIELDS.DESCRIPTION];
        const startDate = value[ASSIGN_TASK_FIELDS.START_DATE];
        const dueDate = value[ASSIGN_TASK_FIELDS.DUE_DATE];
        const errors = {};

        errors[ASSIGN_TASK_FIELDS.NAME] = composeValidators(
          validateRequiredField,
          validateNameLength,
        )(name);
        errors[ASSIGN_TASK_FIELDS.DESCRIPTION] = validateDescriptionLength(description);

        if (startDate && dueDate && !moment.utc(dueDate).isAfter(moment.utc(startDate))) {
          errors[ASSIGN_TASK_FIELDS.DUE_DATE] = <FormattedMessage id="ui-task-list.validation.dueDateAfterStartDate" />;
        }

        errorArray.push(errors);
      });
    }

    return errorArray;
  };

  return (
    <Accordion
      id="assignTask"
      label={<FormattedMessage id="ui-task-list.title.accordion.assignTask" />}
    >
      <FieldArray
        name={JOB_FIELDS.ASSIGN_TASK}
        validate={validate}
      >
        {({ fields }) => fields.map((task, index) => (
          <AssignTask
            key={index}
            canAssignedTaskStatusChange={canAssignedTaskStatusChange}
            fields={fields}
            index={index}
            task={task}
            teams={teams}
            teamsLoaded={teamsLoaded}
            users={users}
            change={change}
            getTeams={getTeams}
            getUsers={getUsers}
          />
        ))}
      </FieldArray>
    </Accordion>
  );
};

AssignTaskList.manifest = Object.freeze({
  teams: {
    type: 'okapi',
    path: 'tasks/teams',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false
  },
  users: {
    type: 'okapi',
    path: 'users',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false
  },
});

AssignTaskList.propTypes = {
  canAssignedTaskStatusChange: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  mutator: PropTypes.shape({
    teams: PropTypes.shape({
      GET: PropTypes.func.isRequired
    }),
    users: PropTypes.shape({
      GET: PropTypes.func.isRequired
    }),
  }),
};

export default stripesConnect(AssignTaskList);
